import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useTheme } from '../contexts/ThemeContext'
import { useAuth } from '../hooks/useAuth'
import { SettingsPageTemplate, SettingsCard, InfoTip, BackToSettingsButton } from '../components/SettingsPageTemplate'
import { API_BASE_URL, buildApiUrl } from '../utils/api'
import { makeApiCall, makeJsonApiCall } from '../utils/customerUtils'

// Types
interface MeetingType {
  id?: number
  name: string
  name_fr?: string
  description: string
  description_fr?: string
  duration: number // in minutes
  color: string
  buffer_before: number // in minutes
  buffer_after: number // in minutes
  is_active: boolean
  sort_order: number
}

interface UrgencyLevel {
  id: number
  name: string
  color: string
  position: number
}

interface NotificationSettings {
  id?: number
  user_id?: number
  // Lead submission
  lead_sms_enabled: boolean
  lead_sms_template_en: string
  lead_sms_template_fr: string
  // Appointment confirmation - Broker
  appointment_broker_sms_enabled: boolean
  appointment_broker_sms_template_en: string
  appointment_broker_sms_template_fr: string
  // Appointment confirmation - Customer
  appointment_customer_sms_enabled: boolean
  appointment_customer_sms_template_en: string
  appointment_customer_sms_template_fr: string
  // Reminder - Broker
  reminder_broker_sms_enabled: boolean
  reminder_broker_sms_template_en: string
  reminder_broker_sms_template_fr: string
  // Reminder - Customer
  reminder_customer_sms_enabled: boolean
  reminder_customer_sms_template_en: string
  reminder_customer_sms_template_fr: string
  // Reminder settings
  reminder_minutes_before: number
  reminders_enabled: boolean
}

interface FormConfiguration {
  id?: number
  user_id?: number
  form_title_en: string
  form_title_fr: string
  form_subtitle_en: string
  form_subtitle_fr: string
  submit_button_en: string
  submit_button_fr: string
  success_title_en: string
  success_title_fr: string
  success_message_en: string
  success_message_fr: string
  preset_id: string
  color_scheme: {
    primary: string
    secondary: string
    accent: string
    background: string
    text: string
    muted: string
  }
  custom_fields: any[]
  show_first_name: boolean
  show_last_name: boolean
  show_email: boolean
  show_phone: boolean
  show_message: boolean
  first_name_label_en: string
  first_name_label_fr: string
  last_name_label_en: string
  last_name_label_fr: string
  email_label_en: string
  email_label_fr: string
  phone_label_en: string
  phone_label_fr: string
  message_label_en: string
  message_label_fr: string
  message_placeholder_en: string
  message_placeholder_fr: string
  custom_css?: string
  // Broker title fields (display info now managed in profile settings)
  broker_title_en?: string
  broker_title_fr?: string
  // Priority fields
  priority_question_en?: string
  priority_question_fr?: string
  priority_urgent_en?: string
  priority_urgent_fr?: string
  priority_medium_en?: string
  priority_medium_fr?: string
  priority_low_en?: string
  priority_low_fr?: string
  // Contact time fields
  contact_time_question_en?: string
  contact_time_question_fr?: string
  contact_time_day_en?: string
  contact_time_day_fr?: string
  contact_time_evening_en?: string
  contact_time_evening_fr?: string
  contact_time_weekend_en?: string
  contact_time_weekend_fr?: string
  // Appointment booking fields
  meeting_type_label_en?: string
  meeting_type_label_fr?: string
  datetime_label_en?: string
  datetime_label_fr?: string
  back_button_en?: string
  back_button_fr?: string
  next_button_en?: string
  next_button_fr?: string
}

interface FormPreset {
  id: number
  preset_id: string
  name_en: string
  name_fr: string
  description_en: string
  description_fr: string
  color_scheme: {
    primary: string
    secondary: string
    accent: string
    background: string
    text: string
    muted: string
  }
  custom_css?: string
  preview_image_url?: string
  is_system_preset: boolean
}

const CustomFormSettings: React.FC = () => {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const { user } = useAuth()

  // State
  const [activeTab, setActiveTab] = useState<'general' | 'contact-form' | 'appointments'>('general')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [savingConfig, setSavingConfig] = useState(false)
  const [savingNotifications, setSavingNotifications] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null)

  // Meeting Types State
  const [meetingTypes, setMeetingTypes] = useState<MeetingType[]>([])
  const [meetingTypeModal, setMeetingTypeModal] = useState<{ isOpen: boolean, editing?: MeetingType | null }>({ isOpen: false })
  const [meetingTypeForm, setMeetingTypeForm] = useState<MeetingType>({
    name: '',
    name_fr: '',
    description: '',
    description_fr: '',
    duration: 30,
    color: '#3B82F6',
    buffer_before: 0,
    buffer_after: 0,
    is_active: true,
    sort_order: 0
  })

  // Form Config State
  const [formConfig, setFormConfig] = useState<FormConfiguration | null>(null)
  const [customFormSlug, setCustomFormSlug] = useState('')
  const [presets, setPresets] = useState<FormPreset[]>([])
  const [urgencyLevels, setUrgencyLevels] = useState<UrgencyLevel[]>([])
  
  // Notification Settings State
  const [notificationSettings, setNotificationSettings] = useState<NotificationSettings | null>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      setLoading(true)
      await Promise.all([
        fetchMeetingTypes(),
        fetchFormConfiguration(),
        fetchFormPresets(),
        fetchUrgencyLevels(),
        fetchCustomFormSlug(),
        fetchNotificationSettings()
      ])
    } catch (error) {
      console.error('Error fetching data:', error)
      showMessage('error', 'Failed to load settings')
    } finally {
      setLoading(false)
    }
  }

  const fetchMeetingTypes = async () => {
    try {
      const token = localStorage.getItem('token')
      if (!token) return

      const response = await fetch(`${API_BASE_URL}/api/calendar/meeting-types`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      
      if (response.ok) {
        const data = await response.json()
        setMeetingTypes(data.meeting_types || [])
      }
    } catch (error) {
      console.error('Error fetching meeting types:', error)
    }
  }

  const fetchFormConfiguration = async () => {
    try {
      const response = await makeApiCall('/api/custom-forms/configuration')
      if (response.ok) {
        const data = await response.json()
        setFormConfig(data)
      }
    } catch (error) {
      console.error('Error fetching form configuration:', error)
    }
  }

  const fetchFormPresets = async () => {
    try {
      const response = await makeApiCall('/api/custom-forms/presets')
      if (response.ok) {
        const data = await response.json()
        setPresets(data)
      }
    } catch (error) {
      console.error('Error fetching form presets:', error)
    }
  }

  const fetchUrgencyLevels = async () => {
    try {
      const response = await makeApiCall('/api/lead-settings/urgency-levels')
      if (response.ok) {
        const data = await response.json()
        setUrgencyLevels(data)
      }
    } catch (error) {
      console.error('Error fetching urgency levels:', error)
    }
  }

  const fetchCustomFormSlug = async () => {
    try {
      const response = await makeApiCall('/api/custom-forms/slug')
      if (response.ok) {
        const data = await response.json()
        setCustomFormSlug(data.custom_form_slug || '')
      }
    } catch (error) {
      console.error('Error fetching custom form slug:', error)
    }
  }

  const fetchNotificationSettings = async () => {
    try {
      const response = await makeApiCall('/api/custom-forms/notification-settings')
      if (response.ok) {
        const data = await response.json()
        setNotificationSettings(data)
      }
    } catch (error) {
      console.error('Error fetching notification settings:', error)
    }
  }

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text })
    setTimeout(() => setMessage(null), 5000)
  }

  // Meeting Types Functions
  const handleSaveMeetingType = async () => {
    try {
      setSaving(true)
      
      if (meetingTypeForm.id) {
        // Update existing meeting type
        const token = localStorage.getItem('token')
        if (!token) return

        const response = await fetch(`${API_BASE_URL}/api/calendar/meeting-types/${meetingTypeForm.id}`, {
          method: 'PUT',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json' 
          },
          body: JSON.stringify(meetingTypeForm)
        })
        
        if (response.ok) {
          showMessage('success', 'Meeting type updated successfully')
          fetchMeetingTypes()
          setMeetingTypeModal({ isOpen: false })
          resetMeetingTypeForm()
        }
      } else {
        // Create new meeting type
        const token = localStorage.getItem('token')
        if (!token) return

        const response = await fetch(`${API_BASE_URL}/api/calendar/meeting-types`, {
          method: 'POST',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json' 
          },
          body: JSON.stringify(meetingTypeForm)
        })
        
        if (response.ok) {
          showMessage('success', 'Meeting type created successfully')
          fetchMeetingTypes()
          setMeetingTypeModal({ isOpen: false })
          resetMeetingTypeForm()
        }
      }
    } catch (error) {
      showMessage('error', 'Failed to save meeting type')
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteMeetingType = async (id: number) => {
    if (!confirm(t('customFormSettings.messages.confirmDelete'))) return

    try {
      const token = localStorage.getItem('token')
      if (!token) return

      const response = await fetch(`${API_BASE_URL}/api/calendar/meeting-types/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      })
      
      if (response.ok) {
        showMessage('success', t('customFormSettings.messages.meetingTypeDeleted'))
        fetchMeetingTypes()
      }
    } catch (error) {
      showMessage('error', 'Failed to delete meeting type')
    }
  }

  const resetMeetingTypeForm = () => {
    setMeetingTypeForm({
      name: '',
      name_fr: '',
      description: '',
      description_fr: '',
      duration: 30,
      color: '#3B82F6',
      buffer_before: 0,
      buffer_after: 0,
      is_active: true,
      sort_order: meetingTypes.length
    })
  }

  const editMeetingType = (meetingType: MeetingType) => {
    setMeetingTypeForm(meetingType)
    setMeetingTypeModal({ isOpen: true, editing: meetingType })
  }

  // Form Configuration Functions
  const handleSaveFormConfig = async () => {
    if (!formConfig) return

    try {
      setSavingConfig(true)
      await makeJsonApiCall('/api/custom-forms/configuration', 'PUT', formConfig)
      showMessage('success', 'Form configuration saved successfully')
    } catch (err) {
      console.error('Error saving form configuration:', err)
      showMessage('error', 'Failed to save form configuration')
    } finally {
      setSavingConfig(false)
    }
  }

  const handleApplyPreset = async (presetId: string) => {
    try {
      setSavingConfig(true)
      const response = await makeApiCall(`/api/custom-forms/apply-preset/${presetId}`, { method: 'POST' })
      if (response.ok) {
        const data = await response.json()
        setFormConfig(data.config)
        showMessage('success', 'Preset applied successfully')
      }
    } catch (err) {
      console.error('Error applying preset:', err)
      showMessage('error', 'Failed to apply preset')
    } finally {
      setSavingConfig(false)
    }
  }

  const updateFormConfig = (updates: Partial<FormConfiguration>) => {
    if (!formConfig) return
    setFormConfig({ ...formConfig, ...updates })
  }

  const updateColorScheme = (colorKey: string, value: string) => {
    if (!formConfig) return
    setFormConfig({
      ...formConfig,
      color_scheme: {
        ...formConfig.color_scheme,
        [colorKey]: value
      }
    })
  }

  // Notification Settings Functions
  const handleSaveNotificationSettings = async () => {
    if (!notificationSettings) return

    try {
      setSavingNotifications(true)
      await makeJsonApiCall('/api/custom-forms/notification-settings', 'PUT', notificationSettings)
      showMessage('success', t('customFormSettings.notifications.saveSuccess'))
    } catch (err) {
      console.error('Error saving notification settings:', err)
      showMessage('error', t('customFormSettings.notifications.saveError'))
    } finally {
      setSavingNotifications(false)
    }
  }

  const updateNotificationSettings = (updates: Partial<NotificationSettings>) => {
    if (!notificationSettings) return
    setNotificationSettings({ ...notificationSettings, ...updates })
  }

  const getCustomFormUrl = () => {
    if (!customFormSlug) return ''
    return `${window.location.origin}/broker/${customFormSlug}`
  }

  const openPreviewTab = () => {
    if (!customFormSlug) {
      showMessage('error', 'No custom form URL configured yet')
      return
    }
    window.open(getCustomFormUrl(), '_blank')
  }

  const colors = [
    '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', 
    '#06B6D4', '#F97316', '#84CC16', '#EC4899', '#6B7280'
  ]

  // Create default form config if none exists
  const defaultFormConfig: FormConfiguration = {
    form_title_en: 'Get In Touch',
    form_title_fr: 'Contactez-nous',
    form_subtitle_en: 'Let us help you with your mortgage needs',
    form_subtitle_fr: 'Laissez-nous vous aider avec vos besoins hypothécaires',
    submit_button_en: 'Send Message',
    submit_button_fr: 'Envoyer le message',
    success_title_en: 'Thank you!',
    success_title_fr: 'Merci!',
    success_message_en: 'We will get back to you soon.',
    success_message_fr: 'Nous vous recontacterons bientôt.',
    preset_id: 'modern',
    color_scheme: {
      primary: '#3B82F6',
      secondary: '#6B7280',
      accent: '#10B981',
      background: '#FFFFFF',
      text: '#1F2937',
      muted: '#9CA3AF'
    },
    custom_fields: [],
    show_first_name: true,
    show_last_name: true,
    show_email: true,
    show_phone: true,
    show_message: true,
    first_name_label_en: 'First Name',
    first_name_label_fr: 'Prénom',
    last_name_label_en: 'Last Name',
    last_name_label_fr: 'Nom de famille',
    email_label_en: 'Email',
    email_label_fr: 'Courriel',
    phone_label_en: 'Phone',
    phone_label_fr: 'Téléphone',
    message_label_en: 'Message',
    message_label_fr: 'Message',
    message_placeholder_en: 'Tell us how we can help...',
    message_placeholder_fr: 'Dites-nous comment nous pouvons vous aider...',
    broker_title_en: 'Mortgage Broker',
    broker_title_fr: 'Courtier hypothécaire'
  }

  // Use default config if none exists
  const activeFormConfig = formConfig || defaultFormConfig

  return (
    <SettingsPageTemplate
      title={t('customFormSettings.title')}
      description={t('customFormSettings.subtitle')}
      message={message}
    >
      <div className="max-w-6xl mx-auto">
        {/* Success/Error Messages */}
        {message && (
          <div className={`mb-6 p-4 rounded-md ${
            message.type === 'success' 
              ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200 border border-green-200 dark:border-green-700' 
              : 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 border border-red-200 dark:border-red-700'
          }`}>
            {message.text}
          </div>
        )}

        {/* Navigation Tabs */}
        <div className="mb-8">
          <nav className="flex space-x-8">
            <button
              onClick={() => setActiveTab('general')}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'general'
                  ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              {t('customFormSettings.tabs.general')}
            </button>
            <button
              onClick={() => setActiveTab('contact-form')}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'contact-form'
                  ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              {t('customFormSettings.tabs.contactForm')}
            </button>
            <button
              onClick={() => setActiveTab('appointments')}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'appointments'
                  ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              {t('customFormSettings.tabs.appointments')}
            </button>
          </nav>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
          </div>
        ) : (
          <>
            {/* Appointments Tab */}
            {activeTab === 'appointments' && (
              <SettingsCard 
                title={t('customFormSettings.meetingTypes.title')}
              >
                <div className="space-y-6">
                  {/* Add New Meeting Type Button */}
                  <div className="flex justify-between items-center">
                    <InfoTip>
                      {t('customFormSettings.meetingTypes.tip')}
                    </InfoTip>
                    <button
                      onClick={() => {
                        resetMeetingTypeForm()
                        setMeetingTypeModal({ isOpen: true })
                      }}
                      className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
                    >
                      ➕ {t('customFormSettings.meetingTypes.addMeetingType')}
                    </button>
                  </div>

                  {/* Meeting Types List */}
                  <div className="space-y-4">
                    {meetingTypes.length === 0 ? (
                      <div className={`text-center py-8 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        <p>{t('customFormSettings.meetingTypes.noMeetingTypes')}</p>
                        <p className="text-sm mt-2">{t('customFormSettings.meetingTypes.addFirstMeetingType')}</p>
                      </div>
                    ) : (
                      meetingTypes.map((meetingType) => (
                        <div
                          key={meetingType.id}
                          className={`p-4 border rounded-lg ${isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                              <div
                                className="w-4 h-4 rounded-full"
                                style={{ backgroundColor: meetingType.color }}
                              />
                              <div>
                                <h4 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                  {meetingType.name}
                                </h4>
                                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                  {meetingType.description}
                                </p>
                                <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'} mt-1`}>
                                  {t('customFormSettings.meetingTypes.duration')}: {meetingType.duration} min
                                  {meetingType.buffer_before > 0 && ` • ${t('customFormSettings.meetingTypes.bufferBefore')}: ${meetingType.buffer_before} min`}
                                  {meetingType.buffer_after > 0 && ` • ${t('customFormSettings.meetingTypes.bufferAfter')}: ${meetingType.buffer_after} min`}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              {!meetingType.is_active && (
                                <span className="px-2 py-1 text-xs rounded bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-400">
                                  {t('customFormSettings.meetingTypes.inactive')}
                                </span>
                              )}
                              <button
                                onClick={() => editMeetingType(meetingType)}
                                className="text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300 text-sm"
                              >
                                {t('customFormSettings.meetingTypes.edit')}
                              </button>
                              <button
                                onClick={() => handleDeleteMeetingType(meetingType.id!)}
                                className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 text-sm ml-2"
                              >
                                {t('customFormSettings.meetingTypes.delete')}
                              </button>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  {/* Appointment Booking Labels */}
                  <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">{t('customFormSettings.textLabels.appointmentBookingLabels')}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <h4 className="font-medium text-gray-900 dark:text-white">{t('customFormSettings.textLabels.english')}</h4>
                        <div className="space-y-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('customFormSettings.textLabels.selectMeetingType')}</label>
                            <input
                              type="text"
                              value={activeFormConfig.meeting_type_label_en || 'Select Meeting Type'}
                              onChange={(e) => updateFormConfig({ meeting_type_label_en: e.target.value })}
                              className="w-full border rounded-md px-3 py-2 text-sm border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('customFormSettings.textLabels.selectDateTime')}</label>
                            <input
                              type="text"
                              value={activeFormConfig.datetime_label_en || 'Select Date & Time'}
                              onChange={(e) => updateFormConfig({ datetime_label_en: e.target.value })}
                              className="w-full border rounded-md px-3 py-2 text-sm border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('customFormSettings.textLabels.backButton')}</label>
                            <input
                              type="text"
                              value={activeFormConfig.back_button_en || 'Back'}
                              onChange={(e) => updateFormConfig({ back_button_en: e.target.value })}
                              className="w-full border rounded-md px-3 py-2 text-sm border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('customFormSettings.textLabels.nextButton')}</label>
                            <input
                              type="text"
                              value={activeFormConfig.next_button_en || 'Next'}
                              onChange={(e) => updateFormConfig({ next_button_en: e.target.value })}
                              className="w-full border rounded-md px-3 py-2 text-sm border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                          </div>
                        </div>
                      </div>
                      <div className="space-y-4">
                        <h4 className="font-medium text-gray-900 dark:text-white">{t('customFormSettings.textLabels.french')}</h4>
                        <div className="space-y-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('customFormSettings.textLabels.selectMeetingTypeFr')}</label>
                            <input
                              type="text"
                              value={activeFormConfig.meeting_type_label_fr || 'Sélectionner le type de rendez-vous'}
                              onChange={(e) => updateFormConfig({ meeting_type_label_fr: e.target.value })}
                              className="w-full border rounded-md px-3 py-2 text-sm border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('customFormSettings.textLabels.selectDateTimeFr')}</label>
                            <input
                              type="text"
                              value={activeFormConfig.datetime_label_fr || 'Sélectionner la date et l\'heure'}
                              onChange={(e) => updateFormConfig({ datetime_label_fr: e.target.value })}
                              className="w-full border rounded-md px-3 py-2 text-sm border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('customFormSettings.textLabels.backButtonFr')}</label>
                            <input
                              type="text"
                              value={activeFormConfig.back_button_fr || 'Précédent'}
                              onChange={(e) => updateFormConfig({ back_button_fr: e.target.value })}
                              className="w-full border rounded-md px-3 py-2 text-sm border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('customFormSettings.textLabels.nextButtonFr')}</label>
                            <input
                              type="text"
                              value={activeFormConfig.next_button_fr || 'Suivant'}
                              onChange={(e) => updateFormConfig({ next_button_fr: e.target.value })}
                              className="w-full border rounded-md px-3 py-2 text-sm border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </SettingsCard>
            )}

            {/* Appointments Tab - SMS & Reminders */}
            {activeTab === 'appointments' && notificationSettings && (
              <SettingsCard title={t('customFormSettings.notifications.title')} className="mt-6">
                <div className="space-y-8">
                  <InfoTip>
                    {t('customFormSettings.notifications.tip')}
                  </InfoTip>

                  {/* Appointment Confirmation - Broker */}
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                        {t('customFormSettings.notifications.appointmentBroker')}
                      </h3>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={notificationSettings.appointment_broker_sms_enabled}
                          onChange={(e) => updateNotificationSettings({ appointment_broker_sms_enabled: e.target.checked })}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 dark:peer-focus:ring-indigo-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-indigo-600"></div>
                      </label>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t('customFormSettings.notifications.smsTemplateEn')}
                        </label>
                        <textarea
                          value={notificationSettings.appointment_broker_sms_template_en}
                          onChange={(e) => updateNotificationSettings({ appointment_broker_sms_template_en: e.target.value })}
                          className="w-full border rounded-md px-3 py-2 text-sm border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          rows={4}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t('customFormSettings.notifications.smsTemplateFr')}
                        </label>
                        <textarea
                          value={notificationSettings.appointment_broker_sms_template_fr}
                          onChange={(e) => updateNotificationSettings({ appointment_broker_sms_template_fr: e.target.value })}
                          className="w-full border rounded-md px-3 py-2 text-sm border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          rows={4}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Appointment Confirmation - Customer */}
                  <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                        {t('customFormSettings.notifications.appointmentCustomer')}
                      </h3>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={notificationSettings.appointment_customer_sms_enabled}
                          onChange={(e) => updateNotificationSettings({ appointment_customer_sms_enabled: e.target.checked })}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 dark:peer-focus:ring-indigo-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-indigo-600"></div>
                      </label>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t('customFormSettings.notifications.smsTemplateEn')}
                        </label>
                        <textarea
                          value={notificationSettings.appointment_customer_sms_template_en}
                          onChange={(e) => updateNotificationSettings({ appointment_customer_sms_template_en: e.target.value })}
                          className="w-full border rounded-md px-3 py-2 text-sm border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          rows={4}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t('customFormSettings.notifications.smsTemplateFr')}
                        </label>
                        <textarea
                          value={notificationSettings.appointment_customer_sms_template_fr}
                          onChange={(e) => updateNotificationSettings({ appointment_customer_sms_template_fr: e.target.value })}
                          className="w-full border rounded-md px-3 py-2 text-sm border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          rows={4}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Reminder Settings */}
                  <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                      {t('customFormSettings.notifications.reminderSettings')}
                    </h3>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            {t('customFormSettings.notifications.enableReminders')}
                          </label>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {t('customFormSettings.notifications.enableRemindersDesc')}
                          </p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={notificationSettings.reminders_enabled}
                            onChange={(e) => updateNotificationSettings({ reminders_enabled: e.target.checked })}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 dark:peer-focus:ring-indigo-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-indigo-600"></div>
                        </label>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t('customFormSettings.notifications.minutesBefore')}
                        </label>
                        <input
                          type="number"
                          value={notificationSettings.reminder_minutes_before}
                          onChange={(e) => updateNotificationSettings({ reminder_minutes_before: parseInt(e.target.value) || 120 })}
                          className="w-full md:w-1/3 border rounded-md px-3 py-2 text-sm border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          min="15"
                          step="15"
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {t('customFormSettings.notifications.minutesBeforeDesc')}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Reminder SMS - Broker */}
                  <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                        {t('customFormSettings.notifications.reminderBroker')}
                      </h3>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={notificationSettings.reminder_broker_sms_enabled}
                          onChange={(e) => updateNotificationSettings({ reminder_broker_sms_enabled: e.target.checked })}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 dark:peer-focus:ring-indigo-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-indigo-600"></div>
                      </label>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t('customFormSettings.notifications.smsTemplateEn')}
                        </label>
                        <textarea
                          value={notificationSettings.reminder_broker_sms_template_en}
                          onChange={(e) => updateNotificationSettings({ reminder_broker_sms_template_en: e.target.value })}
                          className="w-full border rounded-md px-3 py-2 text-sm border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          rows={4}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t('customFormSettings.notifications.smsTemplateFr')}
                        </label>
                        <textarea
                          value={notificationSettings.reminder_broker_sms_template_fr}
                          onChange={(e) => updateNotificationSettings({ reminder_broker_sms_template_fr: e.target.value })}
                          className="w-full border rounded-md px-3 py-2 text-sm border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          rows={4}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Reminder SMS - Customer */}
                  <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                        {t('customFormSettings.notifications.reminderCustomer')}
                      </h3>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={notificationSettings.reminder_customer_sms_enabled}
                          onChange={(e) => updateNotificationSettings({ reminder_customer_sms_enabled: e.target.checked })}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 dark:peer-focus:ring-indigo-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-indigo-600"></div>
                      </label>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t('customFormSettings.notifications.smsTemplateEn')}
                        </label>
                        <textarea
                          value={notificationSettings.reminder_customer_sms_template_en}
                          onChange={(e) => updateNotificationSettings({ reminder_customer_sms_template_en: e.target.value })}
                          className="w-full border rounded-md px-3 py-2 text-sm border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          rows={4}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t('customFormSettings.notifications.smsTemplateFr')}
                        </label>
                        <textarea
                          value={notificationSettings.reminder_customer_sms_template_fr}
                          onChange={(e) => updateNotificationSettings({ reminder_customer_sms_template_fr: e.target.value })}
                          className="w-full border rounded-md px-3 py-2 text-sm border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          rows={4}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </SettingsCard>
            )}

            {/* Appointments Tab - Global Save Button */}
            {activeTab === 'appointments' && (
              <div className="flex justify-end mt-6">
                <button
                  onClick={async () => {
                    await handleSaveFormConfig()
                    if (notificationSettings) {
                      await handleSaveNotificationSettings()
                    }
                  }}
                  disabled={savingConfig || savingNotifications}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-3 rounded-md text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                >
                  {(savingConfig || savingNotifications) ? t('customFormSettings.saving') : t('customFormSettings.saveChanges')}
                </button>
              </div>
            )}

            {/* General Tab - Design Presets */}
            {activeTab === 'general' && (
              <SettingsCard 
                title={t('customFormSettings.designPresets.title')}
              >
                <div className="space-y-6">
                  <div className="flex justify-between items-start">
                    <div>
                      <InfoTip>
                        {t('customFormSettings.designPresets.tip')}
                      </InfoTip>
                      {customFormSlug && (
                        <div className="mt-4">
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{t('customFormSettings.designPresets.yourFormUrl')}</p>
                          <div className="flex items-center space-x-2">
                            <code className={`px-2 py-1 rounded text-sm ${isDark ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-700'}`}>
                              {getCustomFormUrl()}
                            </code>
                            <button
                              onClick={openPreviewTab}
                              className="text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300 text-sm"
                            >
                              🔗 {t('customFormSettings.designPresets.preview')}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                    <button
                      onClick={handleSaveFormConfig}
                      disabled={savingConfig}
                      className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
                    >
                      {savingConfig ? t('customFormSettings.designPresets.saving') : t('customFormSettings.designPresets.saveConfiguration')}
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {/* Custom Preset Option */}
                    <div
                      className={`relative border-2 rounded-lg p-4 cursor-pointer transition-all ${
                        activeFormConfig.preset_id === 'custom'
                          ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                      }`}
                      onClick={() => updateFormConfig({ preset_id: 'custom' })}
                    >
                      <div className="flex items-center space-x-3 mb-3">
                        <div
                          className="w-8 h-8 rounded-full border-2 border-dashed border-gray-400"
                          style={{ background: `linear-gradient(45deg, ${activeFormConfig.color_scheme?.primary || '#3B82F6'}, ${activeFormConfig.color_scheme?.secondary || '#8B5CF6'})` }}
                        />
                        <div>
                          <h4 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {t('customFormSettings.designPresets.custom')}
                          </h4>
                          <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                            {t('customFormSettings.designPresets.customizeColors')}
                          </p>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        {Object.values(activeFormConfig.color_scheme || {}).slice(0, 4).map((color: any, idx) => (
                          <div
                            key={idx}
                            className="w-4 h-4 rounded-full border border-gray-200 dark:border-gray-600"
                            style={{ backgroundColor: color }}
                          />
                        ))}
                      </div>
                      {activeFormConfig.preset_id === 'custom' && (
                        <div className="absolute top-2 right-2">
                          <div className="w-6 h-6 bg-indigo-500 rounded-full flex items-center justify-center">
                            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {presets.map((preset) => (
                      <div
                        key={preset.preset_id}
                        className={`relative border-2 rounded-lg p-4 cursor-pointer transition-all ${
                          activeFormConfig.preset_id === preset.preset_id
                            ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                            : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                        }`}
                        onClick={() => handleApplyPreset(preset.preset_id)}
                      >
                        <div className="flex items-center space-x-3 mb-3">
                          <div
                            className="w-8 h-8 rounded-full"
                            style={{ backgroundColor: typeof preset.color_scheme === 'string' ? JSON.parse(preset.color_scheme).primary : preset.color_scheme.primary }}
                          />
                          <div>
                            <h4 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                              {preset.name_en}
                            </h4>
                            <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                              {preset.description_en}
                            </p>
                          </div>
                        </div>
                        <div className="flex space-x-2">
                          {Object.values(typeof preset.color_scheme === 'string' ? JSON.parse(preset.color_scheme) : preset.color_scheme).slice(0, 4).map((color: any, idx) => (
                            <div
                              key={idx}
                              className="w-4 h-4 rounded-full border border-gray-200 dark:border-gray-600"
                              style={{ backgroundColor: color }}
                            />
                          ))}
                        </div>
                        {activeFormConfig.preset_id === preset.preset_id && (
                          <div className="absolute top-2 right-2">
                            <div className="w-6 h-6 bg-indigo-500 rounded-full flex items-center justify-center">
                              <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Custom Color Editor - Only show when Custom preset is selected */}
                  {activeFormConfig.preset_id === 'custom' && (
                    <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                          {t('customFormSettings.designPresets.customizeColorsTitle')}
                        </h3>
                        <InfoTip>
                          {t('customFormSettings.designPresets.customizeColorsTip')}
                        </InfoTip>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {Object.entries(activeFormConfig.color_scheme || {}).map(([key, value]) => (
                          <div key={key}>
                            <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                              {key.charAt(0).toUpperCase() + key.slice(1)} {t('customFormSettings.designPresets.colorLabel')}
                            </label>
                            <div className="flex items-center space-x-3">
                              <input
                                type="color"
                                value={value}
                                onChange={(e) => updateColorScheme(key, e.target.value)}
                                className="h-10 w-20 border border-gray-300 dark:border-gray-600 rounded-md"
                              />
                              <input
                                type="text"
                                value={value}
                                onChange={(e) => updateColorScheme(key, e.target.value)}
                                className={`flex-1 border rounded-md px-3 py-2 text-sm ${isDark ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-300 bg-white text-gray-900'} focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Custom Form URL */}
                  <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">{t('customFormSettings.formFields.customFormUrl')}</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t('customFormSettings.formFields.customUrlSlug')}
                        </label>
                        <div className="flex">
                          <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-400 text-sm">
                            https://mortgageflow.pro/broker/
                          </span>
                          <input
                            type="text"
                            value={customFormSlug}
                            onChange={(e) => setCustomFormSlug(e.target.value)}
                            placeholder="your-name"
                            className="flex-1 border rounded-r-md px-3 py-2 text-sm border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          />
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {t('customFormSettings.formFields.urlSlugHint')}
                        </p>
                      </div>
                      
                      {customFormSlug && (
                        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-3">
                          <p className="text-sm text-blue-800 dark:text-blue-200 font-medium mb-1">{t('customFormSettings.formFields.yourFormUrl')}</p>
                          <div className="flex items-center space-x-2">
                            <code className="text-sm text-gray-900 dark:text-gray-600">
                              https://mortgageflow.pro/broker/{customFormSlug}
                            </code>
                            <button
                              onClick={() => navigator.clipboard.writeText(`https://mortgageflow.pro/broker/${customFormSlug}`)}
                              className="text-gray-900 dark:text-gray-600 hover:text-blue-800 dark:hover:text-blue-200 text-xs"
                            >
                              {t('customFormSettings.formFields.copy')}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Contact Information Notice */}
                  <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                      <div className="flex items-start">
                        <svg className="w-5 h-5 text-gray-700 dark:text-gray-600 mt-0.5 mr-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <div>
                          <h4 className="text-sm font-medium text-blue-900 dark:text-blue-200 mb-1">
                            {t('customFormSettings.brokerInfo.contactInfoNotice.title')}
                          </h4>
                          <p className="text-sm text-blue-800 dark:text-blue-300">
                            {t('customFormSettings.brokerInfo.contactInfoNotice.description')}{' '}
                            <a 
                              href="/dashboard/settings/profile" 
                              className="font-medium underline hover:text-blue-900 dark:hover:text-blue-200"
                            >
                              {t('customFormSettings.brokerInfo.contactInfoNotice.link')}
                            </a>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Professional Title */}
                  <div className="mt-6">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">{t('customFormSettings.brokerInfo.professionalTitle')}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('customFormSettings.brokerInfo.titleEn')}</label>
                        <input
                          type="text"
                          value={activeFormConfig.broker_title_en || 'Mortgage Broker'}
                          onChange={(e) => updateFormConfig({ broker_title_en: e.target.value })}
                          className="w-full border rounded-md px-3 py-2 text-sm border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('customFormSettings.brokerInfo.titleFr')}</label>
                        <input
                          type="text"
                          value={activeFormConfig.broker_title_fr || 'Courtier hypothécaire'}
                          onChange={(e) => updateFormConfig({ broker_title_fr: e.target.value })}
                          className="w-full border rounded-md px-3 py-2 text-sm border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </SettingsCard>
            )}

            {/* General Tab - Global Save Button */}
            {activeTab === 'general' && (
              <div className="flex justify-end mt-6">
                <button
                  onClick={handleSaveFormConfig}
                  disabled={savingConfig}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-3 rounded-md text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                >
                  {savingConfig ? t('customFormSettings.saving') : t('customFormSettings.saveChanges')}
                </button>
              </div>
            )}

            {/* Contact Form Tab - Text & Labels */}
            {activeTab === 'contact-form' && (
              <SettingsCard title={t('customFormSettings.textLabels.title')}>
                <div className="space-y-8">
                  <InfoTip>
                    {t('customFormSettings.textLabels.tip')}
                  </InfoTip>
                  
                  {/* Form Titles */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">{t('customFormSettings.textLabels.formTitles')}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t('customFormSettings.textLabels.titleEn')}
                        </label>
                        <input
                          type="text"
                          value={activeFormConfig.form_title_en || 'Get In Touch With Me'}
                          onChange={(e) => updateFormConfig({ form_title_en: e.target.value })}
                          className="w-full border rounded-md px-3 py-2 text-sm border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t('customFormSettings.textLabels.titleFr')}
                        </label>
                        <input
                          type="text"
                          value={activeFormConfig.form_title_fr || 'Contactez-moi'}
                          onChange={(e) => updateFormConfig({ form_title_fr: e.target.value })}
                          className="w-full border rounded-md px-3 py-2 text-sm border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t('customFormSettings.textLabels.subtitleEn')}
                        </label>
                        <input
                          type="text"
                          value={activeFormConfig.form_subtitle_en || 'Schedule a meeting with me'}
                          onChange={(e) => updateFormConfig({ form_subtitle_en: e.target.value })}
                          className="w-full border rounded-md px-3 py-2 text-sm border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t('customFormSettings.textLabels.subtitleFr')}
                        </label>
                        <input
                          type="text"
                          value={activeFormConfig.form_subtitle_fr || 'Planifiez un rendez-vous avec moi'}
                          onChange={(e) => updateFormConfig({ form_subtitle_fr: e.target.value })}
                          className="w-full border rounded-md px-3 py-2 text-sm border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Submit Button & Success Messages */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">{t('customFormSettings.textLabels.buttonSuccessMessages')}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t('customFormSettings.textLabels.submitButtonEn')}
                        </label>
                        <input
                          type="text"
                          value={activeFormConfig.submit_button_en || 'Schedule Meeting'}
                          onChange={(e) => updateFormConfig({ submit_button_en: e.target.value })}
                          className="w-full border rounded-md px-3 py-2 text-sm border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t('customFormSettings.textLabels.submitButtonFr')}
                        </label>
                        <input
                          type="text"
                          value={activeFormConfig.submit_button_fr || 'Planifier un rendez-vous'}
                          onChange={(e) => updateFormConfig({ submit_button_fr: e.target.value })}
                          className="w-full border rounded-md px-3 py-2 text-sm border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t('customFormSettings.textLabels.successTitleEn')}
                        </label>
                        <input
                          type="text"
                          value={activeFormConfig.success_title_en || 'Thank you!'}
                          onChange={(e) => updateFormConfig({ success_title_en: e.target.value })}
                          className="w-full border rounded-md px-3 py-2 text-sm border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t('customFormSettings.textLabels.successTitleFr')}
                        </label>
                        <input
                          type="text"
                          value={activeFormConfig.success_title_fr || 'Merci!'}
                          onChange={(e) => updateFormConfig({ success_title_fr: e.target.value })}
                          className="w-full border rounded-md px-3 py-2 text-sm border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                      <div className="md:col-span-1">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t('customFormSettings.textLabels.successMessageEn')}
                        </label>
                        <textarea
                          value={activeFormConfig.success_message_en || 'Your request has been submitted successfully. I will get back to you shortly.'}
                          onChange={(e) => updateFormConfig({ success_message_en: e.target.value })}
                          className="w-full border rounded-md px-3 py-2 text-sm border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          rows={3}
                        />
                      </div>
                      <div className="md:col-span-1">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t('customFormSettings.textLabels.successMessageFr')}
                        </label>
                        <textarea
                          value={activeFormConfig.success_message_fr || 'Votre demande a été soumise avec succès. Je vous recontacterai bientôt.'}
                          onChange={(e) => updateFormConfig({ success_message_fr: e.target.value })}
                          className="w-full border rounded-md px-3 py-2 text-sm border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          rows={3}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Field Labels */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">{t('customFormSettings.textLabels.fieldLabels')}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <h4 className="font-medium text-gray-900 dark:text-white">{t('customFormSettings.textLabels.english')}</h4>
                        <div className="space-y-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('customFormSettings.textLabels.firstNameLabel')}</label>
                            <input
                              type="text"
                              value={activeFormConfig.first_name_label_en || 'First Name'}
                              onChange={(e) => updateFormConfig({ first_name_label_en: e.target.value })}
                              className="w-full border rounded-md px-3 py-2 text-sm border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('customFormSettings.textLabels.lastNameLabel')}</label>
                            <input
                              type="text"
                              value={activeFormConfig.last_name_label_en || 'Last Name'}
                              onChange={(e) => updateFormConfig({ last_name_label_en: e.target.value })}
                              className="w-full border rounded-md px-3 py-2 text-sm border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('customFormSettings.textLabels.emailLabel')}</label>
                            <input
                              type="text"
                              value={activeFormConfig.email_label_en || 'Email Address'}
                              onChange={(e) => updateFormConfig({ email_label_en: e.target.value })}
                              className="w-full border rounded-md px-3 py-2 text-sm border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('customFormSettings.textLabels.phoneLabel')}</label>
                            <input
                              type="text"
                              value={activeFormConfig.phone_label_en || 'Phone Number'}
                              onChange={(e) => updateFormConfig({ phone_label_en: e.target.value })}
                              className="w-full border rounded-md px-3 py-2 text-sm border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('customFormSettings.textLabels.messageLabel')}</label>
                            <input
                              type="text"
                              value={activeFormConfig.message_label_en || 'Message'}
                              onChange={(e) => updateFormConfig({ message_label_en: e.target.value })}
                              className="w-full border rounded-md px-3 py-2 text-sm border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('customFormSettings.textLabels.messagePlaceholder')}</label>
                            <input
                              type="text"
                              value={activeFormConfig.message_placeholder_en || 'Tell me about your needs...'}
                              onChange={(e) => updateFormConfig({ message_placeholder_en: e.target.value })}
                              className="w-full border rounded-md px-3 py-2 text-sm border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                          </div>
                        </div>
                      </div>
                      <div className="space-y-4">
                        <h4 className="font-medium text-gray-900 dark:text-white">{t('customFormSettings.textLabels.french')}</h4>
                        <div className="space-y-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('customFormSettings.textLabels.firstNameLabelFr')}</label>
                            <input
                              type="text"
                              value={activeFormConfig.first_name_label_fr || 'Prénom'}
                              onChange={(e) => updateFormConfig({ first_name_label_fr: e.target.value })}
                              className="w-full border rounded-md px-3 py-2 text-sm border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('customFormSettings.textLabels.lastNameLabelFr')}</label>
                            <input
                              type="text"
                              value={activeFormConfig.last_name_label_fr || 'Nom'}
                              onChange={(e) => updateFormConfig({ last_name_label_fr: e.target.value })}
                              className="w-full border rounded-md px-3 py-2 text-sm border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('customFormSettings.textLabels.emailLabelFr')}</label>
                            <input
                              type="text"
                              value={activeFormConfig.email_label_fr || 'Adresse courriel'}
                              onChange={(e) => updateFormConfig({ email_label_fr: e.target.value })}
                              className="w-full border rounded-md px-3 py-2 text-sm border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('customFormSettings.textLabels.phoneLabelFr')}</label>
                            <input
                              type="text"
                              value={activeFormConfig.phone_label_fr || 'Numéro de téléphone'}
                              onChange={(e) => updateFormConfig({ phone_label_fr: e.target.value })}
                              className="w-full border rounded-md px-3 py-2 text-sm border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('customFormSettings.textLabels.messageLabelFr')}</label>
                            <input
                              type="text"
                              value={activeFormConfig.message_label_fr || 'Message'}
                              onChange={(e) => updateFormConfig({ message_label_fr: e.target.value })}
                              className="w-full border rounded-md px-3 py-2 text-sm border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('customFormSettings.textLabels.messagePlaceholderFr')}</label>
                            <input
                              type="text"
                              value={activeFormConfig.message_placeholder_fr || 'Parlez-moi de vos besoins...'}
                              onChange={(e) => updateFormConfig({ message_placeholder_fr: e.target.value })}
                              className="w-full border rounded-md px-3 py-2 text-sm border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Appointment Booking Labels */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">{t('customFormSettings.textLabels.appointmentBookingLabels')}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <h4 className="font-medium text-gray-900 dark:text-white">{t('customFormSettings.textLabels.english')}</h4>
                        <div className="space-y-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('customFormSettings.textLabels.selectMeetingType')}</label>
                            <input
                              type="text"
                              value={activeFormConfig.meeting_type_label_en || 'Select Meeting Type'}
                              onChange={(e) => updateFormConfig({ meeting_type_label_en: e.target.value })}
                              className="w-full border rounded-md px-3 py-2 text-sm border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('customFormSettings.textLabels.selectDateTime')}</label>
                            <input
                              type="text"
                              value={activeFormConfig.datetime_label_en || 'Select Date & Time'}
                              onChange={(e) => updateFormConfig({ datetime_label_en: e.target.value })}
                              className="w-full border rounded-md px-3 py-2 text-sm border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('customFormSettings.textLabels.backButton')}</label>
                            <input
                              type="text"
                              value={activeFormConfig.back_button_en || 'Back'}
                              onChange={(e) => updateFormConfig({ back_button_en: e.target.value })}
                              className="w-full border rounded-md px-3 py-2 text-sm border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('customFormSettings.textLabels.nextButton')}</label>
                            <input
                              type="text"
                              value={activeFormConfig.next_button_en || 'Next'}
                              onChange={(e) => updateFormConfig({ next_button_en: e.target.value })}
                              className="w-full border rounded-md px-3 py-2 text-sm border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                          </div>
                        </div>
                      </div>
                      <div className="space-y-4">
                        <h4 className="font-medium text-gray-900 dark:text-white">{t('customFormSettings.textLabels.french')}</h4>
                        <div className="space-y-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('customFormSettings.textLabels.selectMeetingTypeFr')}</label>
                            <input
                              type="text"
                              value={activeFormConfig.meeting_type_label_fr || 'Sélectionner le type de rendez-vous'}
                              onChange={(e) => updateFormConfig({ meeting_type_label_fr: e.target.value })}
                              className="w-full border rounded-md px-3 py-2 text-sm border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('customFormSettings.textLabels.selectDateTimeFr')}</label>
                            <input
                              type="text"
                              value={activeFormConfig.datetime_label_fr || 'Sélectionner la date et l\'heure'}
                              onChange={(e) => updateFormConfig({ datetime_label_fr: e.target.value })}
                              className="w-full border rounded-md px-3 py-2 text-sm border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('customFormSettings.textLabels.backButtonFr')}</label>
                            <input
                              type="text"
                              value={activeFormConfig.back_button_fr || 'Précédent'}
                              onChange={(e) => updateFormConfig({ back_button_fr: e.target.value })}
                              className="w-full border rounded-md px-3 py-2 text-sm border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('customFormSettings.textLabels.nextButtonFr')}</label>
                            <input
                              type="text"
                              value={activeFormConfig.next_button_fr || 'Suivant'}
                              onChange={(e) => updateFormConfig({ next_button_fr: e.target.value })}
                              className="w-full border rounded-md px-3 py-2 text-sm border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Field Visibility */}
                  <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">{t('customFormSettings.formFields.fieldVisibility')}</h3>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('customFormSettings.formFields.firstNameField')}</label>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{t('customFormSettings.formFields.firstNameFieldDesc')}</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={activeFormConfig.show_first_name}
                            onChange={(e) => updateFormConfig({ show_first_name: e.target.checked })}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 dark:peer-focus:ring-indigo-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-indigo-600"></div>
                        </label>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div>
                          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('customFormSettings.formFields.emailField')}</label>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{t('customFormSettings.formFields.emailFieldDesc')}</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={activeFormConfig.show_email}
                            onChange={(e) => updateFormConfig({ show_email: e.target.checked })}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 dark:peer-focus:ring-indigo-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-indigo-600"></div>
                        </label>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div>
                          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('customFormSettings.formFields.messageField')}</label>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{t('customFormSettings.formFields.messageFieldDesc')}</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={activeFormConfig.show_message}
                            onChange={(e) => updateFormConfig({ show_message: e.target.checked })}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 dark:peer-focus:ring-indigo-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-indigo-600"></div>
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Lead Submission SMS */}
                  {notificationSettings && (
                    <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                          {t('customFormSettings.notifications.leadSubmission')}
                        </h3>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={notificationSettings.lead_sms_enabled}
                            onChange={(e) => updateNotificationSettings({ lead_sms_enabled: e.target.checked })}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 dark:peer-focus:ring-indigo-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-indigo-600"></div>
                        </label>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            {t('customFormSettings.notifications.smsTemplateEn')}
                          </label>
                          <textarea
                            value={notificationSettings.lead_sms_template_en}
                            onChange={(e) => updateNotificationSettings({ lead_sms_template_en: e.target.value })}
                            className="w-full border rounded-md px-3 py-2 text-sm border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            rows={4}
                            placeholder="New lead from {customerName}..."
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            {t('customFormSettings.notifications.smsTemplateFr')}
                          </label>
                          <textarea
                            value={notificationSettings.lead_sms_template_fr}
                            onChange={(e) => updateNotificationSettings({ lead_sms_template_fr: e.target.value })}
                            className="w-full border rounded-md px-3 py-2 text-sm border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            rows={4}
                            placeholder="Nouveau prospect de {customerName}..."
                          />
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                        {t('customFormSettings.notifications.availableVariables')}: {'{customerName}'}, {'{customerPhone}'}, {'{customerEmail}'}, {'{message}'}
                      </p>
                    </div>
                  )}
                </div>
              </SettingsCard>
            )}

            {/* Contact Form Tab - Global Save Button */}
            {activeTab === 'contact-form' && (
              <div className="flex justify-end mt-6">
                <button
                  onClick={async () => {
                    await handleSaveFormConfig()
                    if (notificationSettings) {
                      await handleSaveNotificationSettings()
                    }
                  }}
                  disabled={savingConfig || savingNotifications}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-3 rounded-md text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                >
                  {(savingConfig || savingNotifications) ? t('customFormSettings.saving') : t('customFormSettings.saveChanges')}
                </button>
              </div>
            )}

          </>
        )}

        {/* Meeting Type Modal */}
        {meetingTypeModal.isOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className={`max-w-md w-full mx-4 p-6 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
              <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {meetingTypeForm.id ? t('customFormSettings.meetingTypes.modal.editTitle') : t('customFormSettings.meetingTypes.modal.addTitle')}
              </h3>
              
              <div className="space-y-4">
                {/* Name English */}
                <div>
                  <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
                    {t('customFormSettings.meetingTypes.modal.nameEn')}
                  </label>
                  <input
                    type="text"
                    value={meetingTypeForm.name}
                    onChange={(e) => setMeetingTypeForm({ ...meetingTypeForm, name: e.target.value })}
                    className={`w-full border rounded-md px-3 py-2 text-sm ${
                      isDark ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-300 bg-white text-gray-900'
                    } focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                    placeholder={t('customFormSettings.meetingTypes.modal.nameEnPlaceholder')}
                  />
                </div>

                {/* Name French */}
                <div>
                  <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
                    {t('customFormSettings.meetingTypes.modal.nameFr')}
                  </label>
                  <input
                    type="text"
                    value={meetingTypeForm.name_fr || ''}
                    onChange={(e) => setMeetingTypeForm({ ...meetingTypeForm, name_fr: e.target.value })}
                    className={`w-full border rounded-md px-3 py-2 text-sm ${
                      isDark ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-300 bg-white text-gray-900'
                    } focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                    placeholder={t('customFormSettings.meetingTypes.modal.nameFrPlaceholder')}
                  />
                </div>

                {/* Description English */}
                <div>
                  <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
                    {t('customFormSettings.meetingTypes.modal.descriptionEn')}
                  </label>
                  <textarea
                    value={meetingTypeForm.description}
                    onChange={(e) => setMeetingTypeForm({ ...meetingTypeForm, description: e.target.value })}
                    className={`w-full border rounded-md px-3 py-2 text-sm ${
                      isDark ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-300 bg-white text-gray-900'
                    } focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                    rows={2}
                    placeholder={t('customFormSettings.meetingTypes.modal.descriptionEnPlaceholder')}
                  />
                </div>

                {/* Description French */}
                <div>
                  <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
                    {t('customFormSettings.meetingTypes.modal.descriptionFr')}
                  </label>
                  <textarea
                    value={meetingTypeForm.description_fr || ''}
                    onChange={(e) => setMeetingTypeForm({ ...meetingTypeForm, description_fr: e.target.value })}
                    className={`w-full border rounded-md px-3 py-2 text-sm ${
                      isDark ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-300 bg-white text-gray-900'
                    } focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                    rows={2}
                    placeholder={t('customFormSettings.meetingTypes.modal.descriptionFrPlaceholder')}
                  />
                </div>

                {/* Duration */}
                <div>
                  <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
                    {t('customFormSettings.meetingTypes.modal.durationMinutes')}
                  </label>
                  <input
                    type="number"
                    value={meetingTypeForm.duration}
                    onChange={(e) => setMeetingTypeForm({ ...meetingTypeForm, duration: parseInt(e.target.value) || 30 })}
                    className={`w-full border rounded-md px-3 py-2 text-sm ${
                      isDark ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-300 bg-white text-gray-900'
                    } focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                    min="15"
                    max="480"
                  />
                </div>

                {/* Color */}
                <div>
                  <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                    {t('customFormSettings.meetingTypes.modal.color')}
                  </label>
                  <div className="flex space-x-2">
                    {colors.map((color) => (
                      <button
                        key={color}
                        onClick={() => setMeetingTypeForm({ ...meetingTypeForm, color })}
                        className={`w-8 h-8 rounded-full border-2 ${
                          meetingTypeForm.color === color ? 'border-gray-400' : 'border-transparent'
                        }`}
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </div>
                </div>

                {/* Buffer Times */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
                      {t('customFormSettings.meetingTypes.modal.bufferBefore')}
                    </label>
                    <input
                      type="number"
                      value={meetingTypeForm.buffer_before}
                      onChange={(e) => setMeetingTypeForm({ ...meetingTypeForm, buffer_before: parseInt(e.target.value) || 0 })}
                      className={`w-full border rounded-md px-3 py-2 text-sm ${
                        isDark ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-300 bg-white text-gray-900'
                      } focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                      min="0"
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
                      {t('customFormSettings.meetingTypes.modal.bufferAfter')}
                    </label>
                    <input
                      type="number"
                      value={meetingTypeForm.buffer_after}
                      onChange={(e) => setMeetingTypeForm({ ...meetingTypeForm, buffer_after: parseInt(e.target.value) || 0 })}
                      className={`w-full border rounded-md px-3 py-2 text-sm ${
                        isDark ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-300 bg-white text-gray-900'
                      } focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                      min="0"
                    />
                  </div>
                </div>

                {/* Active Toggle */}
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={meetingTypeForm.is_active}
                    onChange={(e) => setMeetingTypeForm({ ...meetingTypeForm, is_active: e.target.checked })}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <label className={`ml-2 text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    {t('customFormSettings.meetingTypes.modal.active')}
                  </label>
                </div>
              </div>

              {/* Modal Actions */}
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setMeetingTypeModal({ isOpen: false })}
                  className={`px-4 py-2 text-sm font-medium rounded-md ${
                    isDark ? 'text-gray-300 hover:text-white' : 'text-gray-700 hover:text-gray-900'
                  }`}
                  disabled={saving}
                >
                  {t('customFormSettings.meetingTypes.modal.cancel')}
                </button>
                <button
                  onClick={handleSaveMeetingType}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md text-sm font-medium disabled:opacity-50"
                  disabled={saving || !meetingTypeForm.name.trim()}
                >
                  {saving ? t('customFormSettings.meetingTypes.modal.saving') : (meetingTypeForm.id ? t('customFormSettings.meetingTypes.modal.update') : t('customFormSettings.meetingTypes.modal.create'))}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </SettingsPageTemplate>
  )
}

export default CustomFormSettings